import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Upload, Loader2, CalendarDays, Repeat, Sparkles, Music } from "lucide-react";

// Build a YYYY-MM-DDTHH:mm string for "now" usable as datetime-local min value
function nowLocal() {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 16);
}

function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 16);
}

export default function SubmitEvent() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(editId);

  const [form, setForm] = useState({
    title: "",
    description: "",
    venue_name: "",
    venue_id: "",
    location: "Airlie Beach",
    start_time: "",
    end_time: "",
    price: "",
    booking_url: "",
    category: "event",
    artist: "",
    tagsInput: "",
    is_recurring: false,
    recurrence: "weekly",
  });
  const [image, setImage] = useState("");
  const [venues, setVenues] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState(isEdit);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    api.get("/my/venues").then((r) => setVenues(r.data));
  }, []);

  // Prefill from AI quick-gig or duplicate action — only on first mount when not editing
  useEffect(() => {
    if (isEdit) return;
    const prefillRaw = searchParams.get("prefill") || searchParams.get("duplicate");
    if (!prefillRaw) return;
    try {
      const decoded = JSON.parse(decodeURIComponent(escape(atob(prefillRaw))));
      const ldt = (iso) => {
        if (!iso) return "";
        const d = new Date(iso);
        if (isNaN(d.getTime())) return "";
        const tz = d.getTimezoneOffset() * 60000;
        return new Date(d - tz).toISOString().slice(0, 16);
      };
      setForm((prev) => ({
        ...prev,
        title: decoded.title || "",
        description: decoded.description || "",
        venue_name: decoded.venue_name || "",
        venue_id: decoded.venue_id || "",
        location: decoded.location || "Airlie Beach",
        start_time: ldt(decoded.start_time),
        end_time: ldt(decoded.end_time),
        price: decoded.price || "",
        booking_url: decoded.booking_url || "",
        category: decoded.category || "event",
        artist: decoded.artist || "",
        tagsInput: (decoded.tags || []).join(", "),
        is_recurring: !!decoded.is_recurring,
        recurrence: decoded.recurrence || "weekly",
      }));
      if (decoded.image) setImage(decoded.image);
      toast.success("Pre-filled — review and submit");
    } catch (e) {
      toast.error("Could not load pre-filled event");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/events/${editId}`).then((r) => {
      const e = r.data;
      setForm({
        title: e.title || "",
        description: e.description || "",
        venue_name: e.venue_name || "",
        venue_id: e.venue_id || "",
        location: e.location || "Airlie Beach",
        start_time: isoToLocalInput(e.start_time),
        end_time: isoToLocalInput(e.end_time),
        price: e.price || "",
        booking_url: e.booking_url || "",
        category: e.category || "event",
        artist: e.artist || "",
        tagsInput: (e.tags || []).join(", "),
        is_recurring: !!e.is_recurring,
        recurrence: e.recurrence || "weekly",
      });
      if (e.image) setImage(e.image);
    }).catch(() => toast.error("Could not load event")).finally(() => setLoadingEvent(false));
  }, [isEdit, editId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImage(`${process.env.REACT_APP_BACKEND_URL}${r.data.url}`);
      toast.success("Cover uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.start_time) {
      toast.error("Title and start time are required");
      return;
    }
    setSubmitting(true);
    try {
      const tags = form.tagsInput.split(",").map((t) => t.trim()).filter(Boolean);
      const body = {
        title: form.title,
        description: form.description,
        venue_id: form.venue_id || null,
        venue_name: form.venue_name || null,
        location: form.location,
        start_time: new Date(form.start_time).toISOString(),
        end_time: form.end_time ? new Date(form.end_time).toISOString() : null,
        category: form.category || "event",
        artist: form.artist || null,
        tags,
        image: image || null,
        price: form.price || null,
        booking_url: form.booking_url || null,
        is_recurring: form.is_recurring,
        recurrence: form.is_recurring ? form.recurrence : "",
      };
      if (isEdit) {
        await api.put(`/events/${editId}`, body);
        toast.success("Event updated");
      } else {
        await api.post("/events", body);
        toast.success("Event submitted! Awaiting approval.");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user || loadingEvent) return <Layout><div className="py-20 text-center text-neutral-500">Loading...</div></Layout>;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <CalendarDays className="w-7 h-7 text-coastal" />
          <h1 className="font-heading text-4xl text-navy tracking-tight" data-testid="submit-event-title">
            {isEdit ? "Edit your event" : "Submit an event"}
          </h1>
        </div>
        <p className="text-neutral-500 mb-8">
          {isEdit ? "Update details below. Changes are saved instantly." : "Share a market, gig, tour or party happening in Airlie. Approved within 48h."}
        </p>

        {!isEdit && (
          <button
            type="button"
            onClick={() => navigate("/dashboard/quick-gig")}
            className="w-full mb-6 rounded-xl bg-gradient-to-r from-navy via-navy/95 to-coastal text-white px-5 py-4 flex items-center justify-between hover:opacity-95 transition-opacity"
            data-testid="link-to-quick-gig"
          >
            <span className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-sponsored" />
              <span className="text-sm">Got a live gig? Paste a caption and let AI fill the form for you →</span>
            </span>
          </button>
        )}

        <form onSubmit={submit} className="bg-white rounded-2xl border border-black/5 p-8 space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-navy mb-1.5 block">Type of event</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="event-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="event">General event</SelectItem>
                  <SelectItem value="live_music">
                    <span className="flex items-center gap-2"><Music className="w-3.5 h-3.5" /> Live music</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.category === "live_music" && (
              <div>
                <label className="text-sm font-medium text-navy mb-1.5 block">Artist / band</label>
                <Input
                  placeholder="Dave Helgi Johan"
                  value={form.artist}
                  onChange={(e) => setForm({ ...form, artist: e.target.value })}
                  data-testid="event-artist"
                />
              </div>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-navy mb-1.5 block">Event title</label>
            <Input
              placeholder="Full Moon Beach Party"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              data-testid="event-title"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-navy mb-1.5 block">Description</label>
            <Textarea
              rows={4}
              placeholder="What's happening, who's playing, what makes it special…"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="event-description"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-navy mb-1.5 block">
                {form.is_recurring ? "First occurrence" : "Start (date & time)"}
              </label>
              <Input
                type="datetime-local"
                min={form.is_recurring ? undefined : nowLocal()}
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                required
                data-testid="event-start"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-navy mb-1.5 block">End (optional)</label>
              <Input
                type="datetime-local"
                min={form.start_time || (form.is_recurring ? undefined : nowLocal())}
                value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                data-testid="event-end"
              />
            </div>
          </div>

          {/* Recurring event toggle */}
          <div className="bg-sand/20 rounded-xl p-5 border border-sand/40">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-heading text-lg text-navy flex items-center gap-2">
                  <Repeat className="w-4 h-4" />
                  Recurring event
                </div>
                <p className="text-sm text-neutral-600 mt-0.5">
                  Use this for weekly markets, regular gigs, or anything that repeats. We&apos;ll auto-show the next occurrence on the directory.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setForm({ ...form, is_recurring: !form.is_recurring })}
                className={`relative shrink-0 w-12 h-6 rounded-full transition-colors ${form.is_recurring ? "bg-navy" : "bg-neutral-300"}`}
                data-testid="event-recurring-toggle"
                aria-pressed={form.is_recurring}
              >
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${form.is_recurring ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
            </div>
            {form.is_recurring && (
              <div className="mt-4">
                <label className="text-sm font-medium text-navy mb-1.5 block">Repeats</label>
                <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                  <SelectTrigger data-testid="event-recurrence-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Every week</SelectItem>
                    <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                    <SelectItem value="monthly">Every month (~30 days)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-neutral-500 mt-2">
                  Pick the first occurrence above (past dates allowed). The directory will always show the next upcoming one.
                </p>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-navy mb-1.5 block">Link to one of your venues (optional)</label>
              <Select value={form.venue_id || "__none"} onValueChange={(v) => {
                if (v === "__none") setForm({ ...form, venue_id: "", venue_name: "" });
                else {
                  const match = venues.find((x) => x.id === v);
                  setForm({ ...form, venue_id: v, venue_name: match?.name || "" });
                }
              }}>
                <SelectTrigger data-testid="event-venue-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">None</SelectItem>
                  {venues.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-navy mb-1.5 block">Or venue name (free text)</label>
              <Input
                placeholder="Airlie Foreshore"
                value={form.venue_name}
                onChange={(e) => setForm({ ...form, venue_name: e.target.value, venue_id: "" })}
                disabled={!!form.venue_id}
                data-testid="event-venue-name"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-navy mb-1.5 block">Location</label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                data-testid="event-location"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-navy mb-1.5 block">Price (optional)</label>
              <Input
                placeholder="Free entry, $20, $50 incl drink…"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                data-testid="event-price"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-navy mb-1.5 block">Booking / reservation link (optional)</label>
            <Input
              type="url"
              placeholder="https://yourvenue.com/reservations"
              value={form.booking_url}
              onChange={(e) => setForm({ ...form, booking_url: e.target.value })}
              data-testid="event-booking-url"
            />
            <p className="text-xs text-neutral-500 mt-1.5">
              Adding a link shows a “Reserve” button on the event card so guests can book in one tap.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-navy mb-1.5 block">Tags (comma separated)</label>
            <Input
              placeholder="music, family friendly, outdoor"
              value={form.tagsInput}
              onChange={(e) => setForm({ ...form, tagsInput: e.target.value })}
              data-testid="event-tags"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-navy mb-1.5 block">Cover image (optional)</label>
            <div className="flex items-start gap-4">
              {image ? (
                <div className="relative w-40 h-28 rounded-lg overflow-hidden border border-black/5">
                  <img src={image} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setImage("")}
                    className="absolute top-1 right-1 bg-white/90 rounded-full px-2 text-xs hover:bg-white"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <label className="w-40 h-28 rounded-lg border-2 border-dashed border-navy/20 hover:border-navy flex flex-col items-center justify-center cursor-pointer transition-colors" data-testid="event-upload-label">
                  {uploading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-navy" />
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-navy mb-1" />
                      <span className="text-xs text-navy">Upload cover</span>
                    </>
                  )}
                  <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid="event-upload-input" />
                </label>
              )}
              <p className="text-xs text-neutral-500 pt-2">Recommended: 16:9, min 1200px wide. JPG or PNG.</p>
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full rounded-full bg-navy hover:bg-navy/90"
            data-testid="submit-event-submit"
          >
            {submitting ? "Submitting..." : "Submit for review"}
          </Button>
        </form>
      </div>
    </Layout>
  );
}
